import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Loader, Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useSearchPatients } from 'api/resource';
import { useCurrentSiteStore } from 'store/SiteStore';
import { formatPatientName } from '@billing/validation';

export interface PatientSearchSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  organizationID?: string;
  placeholder?: string;
  label?: string;
  clearable?: boolean;
  searchable?: boolean;
  withinPortal?: boolean;
  disabled?: boolean;
  radius?: string;
  w?: string | number;
  limit?: number;
  dataTestId?: string;
}

export const PatientSearchSelect = forwardRef<
  HTMLInputElement,
  PatientSearchSelectProps
>(
  (
    {
      value,
      onChange,
      organizationID,
      placeholder = 'Search patients',
      label = 'Patient',
      clearable = true,
      searchable = true,
      withinPortal = true,
      disabled = false,
      radius = 'lg',
      w = '100%',
      limit = 20,
      dataTestId,
    },
    ref
  ) => {
    const { organizationID: storeOrganizationID } = useCurrentSiteStore();
    const resolvedOrganizationID = organizationID || storeOrganizationID;
    const [searchValue, setSearchValue] = useState('');
    const [debounced] = useDebouncedValue(searchValue, 500);

    const {
      data: searchResults = [],
      isLoading,
      isFetching,
    } = useSearchPatients({
      searchValue: debounced,
      organizationID: resolvedOrganizationID,
    });

    const options = useMemo(
      () =>
        searchResults.map((patient) => {
          const fullName = formatPatientName(
            patient.firstName,
            patient.lastName
          );
          const phin = patient.number ? ` - PHIN: ${patient.number}` : '';
          const dob = patient.dateOfBirth
            ? ` - DOB: ${patient.dateOfBirth}`
            : '';
          return {
            value: patient.id,
            label: `${fullName}${phin}${dob}`,
          };
        }),
      [searchResults]
    );

    useEffect(() => {
      if (!value) {
        setSearchValue('');
      }
    }, [value]);

    const handleChange = (nextValue: string | null) => {
      onChange(nextValue);
      const label = options.find((option) => option.value === nextValue)?.label;
      setSearchValue(label || '');
    };

    return (
      <Select
        ref={ref}
        placeholder={placeholder}
        data={options}
        label={label}
        value={value}
        searchable={searchable}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onChange={handleChange}
        rightSection={
          isLoading || isFetching ? (
            <Loader size={20} style={{ marginRight: 10 }} />
          ) : null
        }
        nothingFoundMessage={
          searchValue ? 'No patients found' : 'Start typing to search patients'
        }
        clearable={clearable}
        comboboxProps={{ withinPortal }}
        disabled={disabled}
        limit={limit}
        radius={radius as any}
        w={w as any}
        data-testid={dataTestId || 'patient-search-select'}
      />
    );
  }
);
